import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, Play, Check, XCircle, Clock, Warehouse, MapPin, Zap, Split } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/Layout";
import { Chip, ProgressBar } from "@/components/Bits";
import { formatINR } from "@/lib/ui";
import api, { formatApiErrorDetail } from "@/lib/api";

function taskChip(s) {
  if (s === "Completed") return "bg-[#00FF66]/10 border-[#00FF66]/30 text-[#00FF66]";
  if (s === "Blocked") return "bg-white/5 border-white/15 text-zinc-500";
  if (s === "In Progress") return "bg-[#007AFF]/10 border-[#007AFF]/30 text-[#4DA3FF]";
  if (s === "QC Failed") return "bg-[#FF3B30]/10 border-[#FF3B30]/30 text-[#FF6259]";
  return "bg-[#FFD600]/10 border-[#FFD600]/30 text-[#FFD600]";
}

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [o, setO] = useState(null);
  const [tab, setTab] = useState("fulfillment");
  const [routing, setRouting] = useState(null);
  const [alloc, setAlloc] = useState({});
  const [splitMode, setSplitMode] = useState(false);

  const load = useCallback(() => api.get(`/orders/${id}`).then((r) => setO(r.data)).catch(() => navigate("/orders")), [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const loadRouting = useCallback(() => api.get(`/orders/${id}/routing`).then((r) => {
    setRouting(r.data);
    const a = {};
    (r.data.fulfillment_groups || []).forEach((g) => (g.items || []).forEach((it) => { a[it.sku] = g.hub_id; }));
    setAlloc(a);
  }).catch(() => {}), [id]);
  useEffect(() => { if (tab === "routing") loadRouting(); }, [tab, loadRouting]);

  const assign = async (hubId) => {
    try { await api.post(`/orders/${id}/assign-hub?hub_id=${hubId}`); toast.success("Hub assigned · stock reserved"); load(); loadRouting(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const autoRoute = async () => {
    try { await api.post(`/orders/${id}/auto-route`); toast.success("Auto-routed (smart split if needed)"); load(); loadRouting(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const applyAllocation = async () => {
    const byHub = {};
    Object.entries(alloc).forEach(([sku, hub]) => { if (hub) (byHub[hub] = byHub[hub] || []).push(sku); });
    const lines = Object.entries(byHub).map(([hub_id, skus]) => ({ hub_id, skus }));
    try { await api.post(`/orders/${id}/set-allocation`, lines); toast.success("Split allocation applied"); setSplitMode(false); load(); loadRouting(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const act = async (taskId, status) => {
    try { await api.post(`/order-tasks/${taskId}/status?status=${encodeURIComponent(status)}`); toast.success(`Task → ${status}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!o) return <div className="p-8 text-zinc-500 font-mono text-sm">Loading order…</div>;
  const idMap = Object.fromEntries((o.tasks || []).map((t) => [t.id, t.title]));

  return (
    <div>
      <PageHeader title={o.ref} subtitle={`${o.brand} · ${o.channel} · ${o.brand_order_id}`}
        actions={<div className="flex items-center gap-3"><Chip className={o.status === "Completed" ? "bg-[#00FF66]/10 border-[#00FF66]/30 text-[#00FF66]" : "bg-[#007AFF]/10 border-[#007AFF]/30 text-[#4DA3FF]"}>{o.status}</Chip>
          <button data-testid="back-to-orders" onClick={() => navigate("/orders")} className="flex items-center gap-2 text-sm border border-white/15 text-zinc-300 hover:bg-white/5 px-4 py-2 rounded-sm transition-colors"><ArrowLeft size={15} /> Orders</button></div>} />
      <div className="p-8 space-y-6">
        {/* progress */}
        <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
          <div className="flex items-center justify-between mb-3"><span className="text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500">Order Progress · {o.current_stage}</span><span className="font-display font-bold text-lg">{o.progress}%</span></div>
          <ProgressBar value={o.progress} />
        </div>

        <div className="flex gap-2 border-b border-white/10">
          {["fulfillment", "routing", "overview", "timeline", "finance"].map((t) => (
            <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)} className={`px-4 py-2 text-sm font-mono uppercase tracking-wider border-b-2 -mb-px transition-colors ${tab === t ? "border-white text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>{t}</button>
          ))}
        </div>

        {tab === "fulfillment" && (
          <div className="space-y-2">
            {(o.tasks || []).map((t) => (
              <div key={t.id} data-testid={`otask-${t.id}`} className="bg-[#121212] border border-white/10 rounded-sm p-4 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] text-zinc-600">{t.task_id}</span>
                    <span className="text-sm text-zinc-200">{t.title}</span>
                    {t.assigned_team && <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">· {t.assigned_team}</span>}
                  </div>
                  {t.depends_on?.length > 0 && <div className="text-[10px] text-zinc-600 mt-1">after: {t.depends_on.map((d) => idMap[d]).filter(Boolean).join(", ")}</div>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Chip className={taskChip(t.status)}>{t.status}</Chip>
                  {t.status === "Blocked" && <Lock size={15} className="text-zinc-600" />}
                  {t.status === "Assigned" && <button data-testid={`start-${t.id}`} onClick={() => act(t.id, "In Progress")} className="flex items-center gap-1 text-xs bg-[#007AFF]/10 border border-[#007AFF]/30 text-[#4DA3FF] px-2 py-1 rounded-sm hover:bg-[#007AFF]/20"><Play size={12} /> Start</button>}
                  {t.status === "In Progress" && <><button data-testid={`complete-${t.id}`} onClick={() => act(t.id, "Completed")} className="flex items-center gap-1 text-xs bg-[#00FF66]/10 border border-[#00FF66]/30 text-[#00FF66] px-2 py-1 rounded-sm hover:bg-[#00FF66]/20"><Check size={12} /> Complete</button>
                    {t.category === "QC" && <button onClick={() => act(t.id, "QC Failed")} className="flex items-center gap-1 text-xs bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF6259] px-2 py-1 rounded-sm hover:bg-[#FF3B30]/20"><XCircle size={12} /> Fail</button>}</>}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "routing" && (
          <div className="space-y-6">
            <div className="bg-[#121212] border border-white/10 rounded-sm p-6 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Warehouse size={22} className="text-zinc-400" />
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">Fulfillment</div>
                  <div className="font-display text-lg">{routing?.assigned_hub_name || "Not assigned"}</div>
                </div>
                {routing?.inventory_state && (
                  <Chip className={routing.inventory_state === "Deducted" ? "bg-[#00FF66]/10 border-[#00FF66]/30 text-[#00FF66]" : routing.inventory_state === "Reserved" ? "bg-[#FFD600]/10 border-[#FFD600]/30 text-[#FFD600]" : "bg-white/5 border-white/15 text-zinc-400"}>
                    {routing.inventory_state}
                  </Chip>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="auto-route-button" onClick={autoRoute} className="flex items-center gap-1.5 text-xs bg-white text-black font-medium px-3 py-2 rounded-sm hover:bg-zinc-200 transition-colors"><Zap size={13} /> Auto-route</button>
                <button data-testid="split-toggle-button" onClick={() => setSplitMode((s) => !s)} className="flex items-center gap-1.5 text-xs border border-white/15 text-zinc-300 px-3 py-2 rounded-sm hover:bg-white/5 transition-colors"><Split size={13} /> {splitMode ? "Cancel split" : "Split manually"}</button>
              </div>
            </div>

            {routing?.is_split && routing?.fulfillment_groups?.length > 0 && !splitMode && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {routing.fulfillment_groups.map((g) => (
                  <div key={g.group_id} data-testid={`fgroup-${g.group_id}`} className="bg-[#121212] border border-white/10 rounded-sm p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-zinc-200 flex items-center gap-1.5"><Warehouse size={13} className="text-zinc-500" /> {g.hub_name}</span>
                      <Chip className={g.inventory_state === "Deducted" ? "bg-[#00FF66]/10 border-[#00FF66]/30 text-[#00FF66]" : "bg-[#FFD600]/10 border-[#FFD600]/30 text-[#FFD600]"}>{g.status}</Chip>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(g.items || []).map((it, i) => <span key={i} className="text-[11px] font-mono border border-white/10 rounded-sm px-2 py-1 text-zinc-300">{it.sku} × {it.qty}</span>)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {splitMode ? (
              <div className="bg-[#121212] border border-white/10 rounded-sm p-6 space-y-3">
                <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500">Manual split · assign each SKU to a hub</div>
                {(routing?.items || []).map((it) => (
                  <div key={it.sku} className="flex items-center justify-between gap-3">
                    <span className="text-sm text-zinc-300">{it.name} {it.variant && `(${it.variant})`} <span className="font-mono text-zinc-500">· {it.sku} × {it.qty}</span></span>
                    <select data-testid={`split-sku-${it.sku}`} value={alloc[it.sku] || ""} onChange={(e) => setAlloc((a) => ({ ...a, [it.sku]: e.target.value }))}
                      className="bg-[#050505] border border-white/15 text-sm text-white rounded-sm px-3 py-1.5 focus:outline-none focus:border-white">
                      <option value="">Select hub…</option>
                      {(routing?.candidates || []).map((c) => <option key={c.hub_id} value={c.hub_id}>{c.hub_name}</option>)}
                    </select>
                  </div>
                ))}
                <button data-testid="apply-split-button" onClick={applyAllocation} className="w-full text-sm bg-white text-black font-medium py-2.5 rounded-sm hover:bg-zinc-200 transition-colors mt-2">Apply Split Allocation</button>
              </div>
            ) : (
              <div>
                <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-3">Smart Routing · ranked by stock → proximity → SLA → load</div>
                <div className="space-y-2">
                  {(routing?.candidates || []).length === 0 && <div className="text-sm text-zinc-500 font-mono">No eligible hubs for this brand.</div>}
                  {(routing?.candidates || []).map((c, i) => (
                    <div key={c.hub_id} data-testid={`route-cand-${c.hub_id}`} className={`bg-[#121212] border rounded-sm p-4 flex flex-wrap items-center justify-between gap-4 ${i === 0 && c.can_fulfill ? "border-[#00FF66]/40" : "border-white/10"}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          {i === 0 && c.can_fulfill && <Zap size={14} className="text-[#00FF66]" />}
                          <span className="text-sm text-zinc-200">{c.hub_name}</span>
                          <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">· {c.type}</span>
                          {routing?.assigned_hub_id === c.hub_id && <Chip className="bg-[#007AFF]/10 border-[#007AFF]/30 text-[#4DA3FF]">Current</Chip>}
                        </div>
                        <div className="text-[11px] text-zinc-500 mt-1 flex items-center gap-3 flex-wrap font-mono">
                          <span className="flex items-center gap-1"><MapPin size={10} /> {c.city || "—"}</span>
                          <span>SLA {c.sla_days}d</span>
                          <span>Load {c.load_pct}%</span>
                          <span className={c.can_fulfill ? "text-[#00FF66]" : "text-[#FF6259]"}>Stock {c.coverage_pct}%</span>
                          {!c.can_fulfill && c.missing?.length > 0 && <span className="text-[#FF6259]">missing: {c.missing.join(", ")}</span>}
                        </div>
                      </div>
                      <button data-testid={`assign-hub-${c.hub_id}`} disabled={!c.can_fulfill}
                        onClick={() => assign(c.hub_id)}
                        className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border transition-colors ${!c.can_fulfill ? "border-white/10 text-zinc-600 cursor-not-allowed" : "bg-white text-black border-white font-medium hover:bg-zinc-200"}`}>
                        <Warehouse size={13} /> Assign all here
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "overview" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-[#121212] border border-white/10 rounded-sm p-6 space-y-2 text-sm">
              <h3 className="font-display text-lg mb-2">Customer</h3>
              <div className="text-zinc-400">{o.customer_name} · {o.customer_phone}</div>
              <div className="text-zinc-500">{o.customer_email}</div>
              <div className="text-zinc-500">{o.delivery_address}</div>
              <div className="text-zinc-500 pt-2">Payment: <span className="text-zinc-300">{o.payment_status} · {o.payment_method}</span></div>
            </div>
            <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
              <h3 className="font-display text-lg mb-3">Items</h3>
              {(o.items || []).map((it, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 text-sm">
                  <span className="text-zinc-300">{it.name} {it.variant && `(${it.variant})`} × {it.qty}</span>
                  <span className="font-mono text-zinc-400">{formatINR(it.unit_price * it.qty)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between pt-3 font-display font-bold"><span>Total</span><span>{formatINR(o.total)}</span></div>
            </div>
          </div>
        )}

        {tab === "timeline" && (
          <div className="bg-[#121212] border border-white/10 rounded-sm p-6 space-y-3">
            {(o.timeline || []).slice().reverse().map((e, i) => (
              <div key={i} className="flex gap-3">
                <div className="w-2 h-2 rounded-full bg-white mt-1.5 shrink-0" />
                <div><div className="text-sm text-zinc-200">{e.event}</div>{e.note && <div className="text-xs text-zinc-500">{e.note}</div>}<div className="text-[10px] font-mono uppercase tracking-wider text-zinc-600 mt-0.5 flex items-center gap-1"><Clock size={10} /> {e.by} · {(e.at || "").slice(0, 16).replace("T", " ")}</div></div>
              </div>
            ))}
          </div>
        )}

        {tab === "finance" && o.finance && (
          <div className="bg-[#121212] border border-white/10 rounded-sm p-6 max-w-md">
            {[["Revenue", o.finance.revenue], ["Product / Hub cost", -o.finance.product_cost], ["Packaging", -o.finance.packaging], ["Delivery", -o.finance.delivery], ["Fees", -o.finance.fees], ["Refund", -o.finance.refund]].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between py-2 border-b border-white/5 text-sm"><span className="text-zinc-400">{k}</span><span className={`font-mono ${v < 0 ? "text-[#FF6259]" : "text-zinc-200"}`}>{formatINR(v)}</span></div>
            ))}
            <div className="flex items-center justify-between pt-3 font-display font-bold"><span>Est. Contribution Margin</span><span className="text-[#00FF66]">{formatINR(o.finance.margin)}</span></div>
          </div>
        )}
      </div>
    </div>
  );
}
